import $ from 'jquery';

import CardComponent from 'chairisher/component/card';

import { getFixAllAboveError } from 'chairisher/context/error';

/**
 * Super class for individual checkout sections.
 * Each checkout section can override the methods declared here.
 */
class CheckoutSectionView {
    constructor(cardSelector) {
        this.$el = $(cardSelector);
        this.$fieldsEl = this.$el.find('.js-card-content');
        this.$summaryEl = this.$el.find('.js-chairish-card-summary');
        this.cardComponent = new CardComponent(this.$el);
        this.fieldNameToComponentMap = {};
        this.hasClickedNext = false;
        this.valueJSON = '';
        this.shouldCalculateProcessingFee = true;
        /**
         * An object of callback functions, or null when it hasn't been set yet.
         *
         * @see CheckoutView
         */
        this.checkoutCallbacks = null;
    }

    /**
     * Finds a checkout section's step number.
     *
     * @param {jQuery} $cardEl
     * @returns {number}
     */
    static getStepNumberFromCardEl($cardEl) {
        return parseInt($cardEl.data('step-number'), 10) || 0;
    }

    /**
     * Sets callback functions provided by the main CheckoutView.
     *
     * @param {Object<Function>} callbacks
     */
    setCallbacks(callbacks) {
        this.checkoutCallbacks = callbacks;
    }

    /**
     * Binds events to the checkout section.
     */
    bind() {}

    /**
     * Returns the relevant data for the checkout section from the context.
     *
     * @returns {Object}
     */
    getDataFromContext() {}

    /**
     * Creates a mapping of field name to JS FormFieldComponent instance
     */
    populateFieldNameToComponentMap() {}

    /**
     * Renders elements that are visible when the underlying card is summarized.
     */
    renderSummary() {}

    /**
     * Clears error messages in the checkout section
     */
    clearErrors() {
        this.toggleFixAllAboveError(false);
        this.$fieldsEl.find('.errorlist .error-item').remove();
    }

    /**
     * Renders elements that are visible when the underlying card is editable.
     */
    renderForm() {
        Object.values(this.fieldNameToComponentMap).forEach((component) => {
            component.render();
        });
    }

    /**
     * Tracks an error event when the card is submitted for validation and is not error free
     */
    submitErrorAnalytics() {}

    /**
     * Tracks a success event when the card is submitted for validation and is error free
     */
    submitSuccessAnalytics() {}

    /**
     * Runs before the section is updated with new data from the server.
     *
     * @return {$.Deferred}
     */
    beforeUpdate() {
        return $.Deferred().resolve();
    }

    /**
     * Updates the card's form components based on data from the JS context
     */
    updateFormData() {
        Object.entries(this.getDataFromContext() || {}).forEach(([key, value]) => {
            const component = this.fieldNameToComponentMap[key];
            if (component) {
                component.setData(value);
            }
        });
    }

    /**
     * 1. Maps context data field names to form components
     * 2. Updates form components with context data
     * 3. Binds event handlers
     */
    initialize() {
        this.populateFieldNameToComponentMap();
        this.updateFormData();
        this.bind();
    }

    getEl() {
        return this.$el;
    }

    /**
     * Collapses this checkout section
     */
    collapse() {
        this.cardComponent.collapse();
    }

    /**
     * Summarizes this checkout section
     */
    summarize() {
        this.cardComponent.summarize();
    }

    /**
     * Makes this checkout section editable
     */
    edit() {
        this.cardComponent.edit();
    }

    /**
     * @param {boolean} shouldDisable Whether to disable this sections "next" button.
     */
    toggleNextButtonDisabled(shouldDisable) {
        this.$fieldsEl.find('.js-next').prop('disabled', shouldDisable);
    }

    /**
     * @returns {number} This view's step number in the checkout process.
     */
    getStepNumber() {
        return CheckoutSectionView.getStepNumberFromCardEl(this.$el);
    }

    /**
     * Gets the view's data object that's submitted to the backend.
     *
     * @returns {Object|null}
     */
    getValue() {
        const obj = {};
        let hasValue = false;

        Object.keys(this.fieldNameToComponentMap).forEach((key) => {
            const component = this.fieldNameToComponentMap[key];
            const value = component.getValue();
            // N.B. null and undefined checks are needed so 0 and '' aren't excluded.
            if (value !== null && value !== undefined) {
                obj[key] = { value };
                hasValue = true;
            }
        });

        return hasValue ? obj : null;
    }

    /**
     * Stores the json representation of the view's current value.
     * Used to detect whether the user has changed any data in the view.
     */
    setValueJSON() {
        this.valueJSON = JSON.stringify(this.getValue());
    }

    /**
     * Clears the value JSON - this will force the user to progress through subsequent sections.
     */
    clearValueJSON() {
        this.valueJSON = '';
    }

    /**
     * @returns {boolean} Whether the view's value has changed since last time this was called.
     */
    isDirty() {
        const lastValueJSON = this.valueJSON;
        this.setValueJSON();
        return this.valueJSON !== lastValueJSON;
    }

    setHasClickedNext(hasClickedNext) {
        this.hasClickedNext = hasClickedNext;
    }

    /**
     * @returns {boolean} True indicates every component in the view is valid.
     */
    isValid() {
        return Object.values(this.fieldNameToComponentMap).every((component) => component.isValid());
    }

    /**
     * Toggles whether to show the 'please fix all above errors' error.
     *
     * @param {boolean} shouldShow
     */
    toggleFixAllAboveError(shouldShow) {
        this.$fieldsEl.find('.js-fix-all-above-error').text(shouldShow ? getFixAllAboveError() : '');
    }

    renderErrors() {
        Object.values(this.fieldNameToComponentMap).forEach((component) => {
            component.renderErrors();
        });
        this.toggleFixAllAboveError(!this.isValid());
    }
}

export default CheckoutSectionView;
